import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { flushSync } from "react-dom";
import { useQuery, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { api, ApiError, call } from "../api/client";
import type { CampaignHead, CampaignKind, CampaignOperation, CampaignOperationResult, DossierView } from "./types";

/** What the campaign is read in: the normal view of its role, the player view a game master switched on, or the dossier. */
export type CampaignViewMode = "normal" | "player" | "dossier";

type ViewHeaders = Record<string, string>;
type Sent<T> = Promise<{ data?: T; error?: unknown; response: Response }>;

export const conflictMessage = "Die Daten wurden gleichzeitig geändert. Bitte neu laden.";

/** A request of one view whose answer arrives after the view changed; its data is dropped, so nothing of the dossier lingers. */
export const viewChangedMessage = "Die Ansicht wurde gewechselt, bevor die Antwort kam. Nichts davon wird angezeigt.";

/** An operation still waiting when its view ended is not sent: in another view it would create something else (known instead of secret). */
export const notSentMessage = "Die Ansicht wurde gewechselt, bevor die Änderung gesendet war. Sie wurde nicht gespeichert.";

/** The view a request was sent in: the dossier token, the player view or the normal view. */
function viewOf(headers: ViewHeaders): string {
  return headers["X-Dossier"] ?? headers["X-Ansicht"] ?? "";
}

interface Dossier {
  token: string;
  /** Local time in ms at which the dossier ends without further input. */
  deadline: number;
}

export interface CampaignContextValue {
  id: string;
  head: CampaignHead;
  mode: CampaignViewMode;
  /** The user leads the campaign, whatever the view. */
  leads: boolean;
  /** Changes are possible in this view; players write nothing (F8). */
  canWrite: boolean;
  /** Query key under this campaign and view, so data of one view never shows in another. */
  key: (...parts: unknown[]) => QueryKey;
  /** Sends a request with the headers of the view. */
  request: <T>(send: (headers: ViewHeaders) => Sent<T>) => Promise<T>;
  /** A plain fetch with the headers of the view, for uploads and files; in the dossier it is aborted when the dossier ends. */
  fetch: (url: string, init?: RequestInit) => Promise<Response>;
  /** The current view, to compare with {@link sameView} before using what arrived for it. */
  view: () => string;
  sameView: (sent: string) => boolean;
  /** Marks an open form with unsaved input until the returned function is called; switching the view warns about it. */
  trackDraft: () => () => void;
  hasDrafts: () => boolean;
  apply: (operation: CampaignOperation) => Promise<CampaignOperationResult>;
  undo: () => Promise<void>;
  redo: () => Promise<void>;
  /** Reloads everything of the campaign in this view. */
  reload: () => void;
  term: (key: string) => string;
  kind: (id: string) => CampaignKind | undefined;
  kindLabel: (id: string) => string;
  entryPath: (entryId: string) => string;
  dossierDeadline: number | null;
  openDossier: () => Promise<void>;
  closeDossier: (notice?: string) => void;
  setPlayerView: (on: boolean) => void;
  notice: string | null;
  clearNotice: () => void;
}

const CampaignContext = createContext<CampaignContextValue | null>(null);

export function useCampaign(): CampaignContextValue {
  const context = useContext(CampaignContext);
  if (!context) throw new Error("useCampaign außerhalb einer Kampagne");
  return context;
}

/** A query of the campaign in the current view. */
export function useCampaignQuery<T>(parts: unknown[], send: (headers: ViewHeaders) => Sent<T>, enabled = true) {
  const campaign = useCampaign();
  return useQuery({ queryKey: campaign.key(...parts), queryFn: () => campaign.request(send), enabled });
}

/** Registers unsaved input of a form, so switching the view asks before it is lost. */
export function useDraft(dirty: boolean) {
  const { trackDraft } = useCampaign();
  useEffect(() => (dirty ? trackDraft() : undefined), [dirty, trackDraft]);
}

/** Time between two touches of the server that keep the dossier open while the game master works without saving. */
const touchEvery = 30_000;

/**
 * Holds the view of one campaign. The dossier token lives only in this component's memory, never in storage or the URL. Ending the
 * dossier, by the button, by time without input, by a 403 or by leaving the campaign or the page, revokes the token on the server,
 * drops every query read in the dossier and remounts the pages below, so no secret stays in state, cache or the page itself.
 */
export function CampaignProvider(props: { id: string; children: (head: CampaignHead | undefined, error: Error | null, epoch: number) => ReactNode }) {
  const { id } = props;
  const client = useQueryClient();
  const [dossier, setDossier] = useState<Dossier | null>(null);
  const [playerView, setPlayerViewState] = useState(false);
  const [epoch, setEpoch] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);
  const dossierRef = useRef<Dossier | null>(null);
  dossierRef.current = dossier;
  const playerRef = useRef(false);
  playerRef.current = playerView;
  const lastTouch = useRef(0);
  const queue = useRef<Promise<unknown>>(Promise.resolve());
  const dossierFetches = useRef(new Set<AbortController>());
  const drafts = useRef(new Set<symbol>());

  const viewKey = dossier ? `dossier-${epoch}` : playerView ? "player" : "normal";
  const key = useCallback((...parts: unknown[]): QueryKey => ["campaign", id, viewKey, ...parts], [id, viewKey]);

  const dropDossierData = useCallback(() => {
    const predicate = (q: { queryKey: QueryKey }) => q.queryKey[0] === "campaign" && String(q.queryKey[2]).startsWith("dossier");
    void client.cancelQueries({ predicate });
    client.removeQueries({ predicate });
  }, [client]);

  const closeDossier = useCallback(
    (message?: string) => {
      const current = dossierRef.current;
      if (!current) return;
      dossierRef.current = null;
      for (const controller of dossierFetches.current) controller.abort();
      dossierFetches.current.clear();
      void fetch(`/api/campaigns/${id}/dossier`, { method: "DELETE", headers: { "X-Dossier": current.token }, credentials: "include", keepalive: true }).catch(
        () => undefined,
      );
      setDossier(null);
      setEpoch((e) => e + 1);
      setNotice(message ?? null);
      dropDossierData();
    },
    [id, dropDossierData],
  );

  const headers = useCallback((): ViewHeaders => {
    const current = dossierRef.current;
    if (current) return { "X-Dossier": current.token };
    return playerRef.current ? { "X-Ansicht": "spieler" } : {};
  }, []);

  /** Learns the new expiry of the dossier from a response, or ends the dossier when the server refused its token. */
  const observe = useCallback(
    (response: Response, sent: ViewHeaders, sentAt: number) => {
      const token = sent["X-Dossier"];
      if (!token || dossierRef.current?.token !== token) return;
      if (response.status === 403) {
        closeDossier("Das Dossier ist abgelaufen und wurde ausgeblendet.");
        return;
      }
      const expires = Date.parse(response.headers.get("X-Dossier-Ablauf") ?? "");
      if (Number.isNaN(expires)) return;
      lastTouch.current = Date.now();
      const deadline = localDeadline(expires, response, sentAt);
      setDossier((d) => (d && d.token === token ? { ...d, deadline } : d));
    },
    [closeDossier],
  );

  const view = useCallback(() => viewOf(headers()), [headers]);
  const sameView = useCallback((sent: string) => viewOf(headers()) === sent, [headers]);

  const request = useCallback(
    async <T,>(send: (headers: ViewHeaders) => Sent<T>): Promise<T> => {
      const sent = headers();
      const sentAt = Date.now();
      const promise = send(sent);
      const { response } = await promise;
      observe(response, sent, sentAt);
      if (response.status === 409) {
        await promise.catch(() => undefined);
        throw new ApiError(409, conflictMessage);
      }
      const data = await call(promise);
      if (!sameView(viewOf(sent))) throw new ApiError(0, viewChangedMessage);
      return data;
    },
    [headers, observe, sameView],
  );

  const fetchWithView = useCallback(
    async (url: string, init: RequestInit = {}) => {
      const sent = headers();
      const merged = new Headers(init.headers);
      for (const [name, value] of Object.entries(sent)) merged.set(name, value);
      // A dossier fetch ends with the dossier, its body included, so a file still loading is never saved afterwards.
      const controller = sent["X-Dossier"] ? new AbortController() : null;
      if (controller) dossierFetches.current.add(controller);
      const sentAt = Date.now();
      const response = await fetch(url, { ...init, credentials: "include", headers: merged, signal: controller?.signal ?? init.signal });
      observe(response, sent, sentAt);
      return response;
    },
    [headers, observe],
  );

  const head = useQuery({
    queryKey: key("head"),
    queryFn: () => request((h) => api.GET("/api/campaigns/{id}", { params: { path: { id } }, headers: h })),
  });

  const refresh = useCallback(
    (result: CampaignHead) => {
      client.setQueryData(key("head"), result);
      void client.invalidateQueries({ queryKey: ["campaign", id, viewKey], predicate: (q) => q.queryKey[3] !== "head" });
      void client.invalidateQueries({ queryKey: ["campaigns"] });
    },
    [client, key, id, viewKey],
  );

  /**
   * Operations, undo and redo run one after another, each with the revision its predecessor left, so a change elsewhere gives
   * 409 and undo takes back the save before it. One still waiting when its view ended is not sent.
   */
  const enqueue = useCallback(
    <T,>(send: () => Promise<T>) => {
      const enqueued = view();
      const next = queue.current.catch(() => undefined).then(() => {
        if (!sameView(enqueued)) throw new ApiError(0, notSentMessage);
        return send();
      });
      queue.current = next;
      return next;
    },
    [view, sameView],
  );

  const apply = useCallback(
    (operation: CampaignOperation) =>
      enqueue(async () => {
        const revision = client.getQueryData<CampaignHead>(key("head"))?.revision ?? null;
        const result = await request((h) =>
          api.POST("/api/campaigns/{id}/operations", {
            params: { path: { id } },
            headers: h,
            body: { operation: { type: operation.type, payload: operation.payload }, expectedRevision: revision },
          }),
        );
        refresh(result.campaign);
        return result;
      }),
    [client, key, request, refresh, id, enqueue],
  );

  const step = useCallback(
    (direction: "undo" | "redo") =>
      enqueue(async () => {
        const result = await request((h) =>
          direction === "undo"
            ? api.POST("/api/campaigns/{id}/undo", { params: { path: { id } }, headers: h })
            : api.POST("/api/campaigns/{id}/redo", { params: { path: { id } }, headers: h }),
        );
        refresh(result);
      }),
    [request, refresh, id, enqueue],
  );

  const openDossier = useCallback(async () => {
    setPlayerViewState(false);
    const sentAt = Date.now();
    const promise = api.POST("/api/campaigns/{id}/dossier", { params: { path: { id } } });
    const { response } = await promise;
    const opened = await call<DossierView>(promise);
    lastTouch.current = Date.now();
    setNotice(null);
    setDossier({ token: opened.token, deadline: localDeadline(Date.parse(opened.expiresAt), response, sentAt) });
  }, [id]);

  const setPlayerView = useCallback(
    (on: boolean) => {
      if (on) closeDossier();
      setPlayerViewState(on);
    },
    [closeDossier],
  );

  // Time without input ends the dossier; the countdown in the head follows the same clock.
  useEffect(() => {
    if (!dossier) return;
    const timer = setInterval(() => {
      if (dossierRef.current && Date.now() >= dossierRef.current.deadline) closeDossier("Das Dossier wurde nach der Dossierzeit ohne Eingabe ausgeblendet.");
    }, 1000);
    return () => clearInterval(timer);
  }, [dossier, closeDossier]);

  // Input without saving keeps the dossier open: now and then a read touches the server, which extends the token.
  useEffect(() => {
    if (!dossier) return;
    const onInput = () => {
      if (Date.now() - lastTouch.current < touchEvery) return;
      lastTouch.current = Date.now();
      void client.invalidateQueries({ queryKey: key("head") });
    };
    document.addEventListener("keydown", onInput, true);
    document.addEventListener("pointerdown", onInput, true);
    return () => {
      document.removeEventListener("keydown", onInput, true);
      document.removeEventListener("pointerdown", onInput, true);
    };
  }, [dossier, client, key]);

  // Leaving the page ends the dossier before the browser keeps the page for the back button.
  useEffect(() => {
    const leave = () => {
      if (dossierRef.current) flushSync(() => closeDossier());
    };
    const back = (e: PageTransitionEvent) => {
      if (e.persisted) dropDossierData();
    };
    window.addEventListener("pagehide", leave);
    window.addEventListener("pageshow", back);
    return () => {
      window.removeEventListener("pagehide", leave);
      window.removeEventListener("pageshow", back);
    };
  }, [closeDossier, dropDossierData]);

  // Leaving the campaign ends the dossier.
  useEffect(() => () => closeDossier(), [closeDossier]);

  const trackDraft = useCallback(() => {
    const token = Symbol("draft");
    drafts.current.add(token);
    return () => void drafts.current.delete(token);
  }, []);
  const hasDrafts = useCallback(() => drafts.current.size > 0, []);

  const value = useMemo<CampaignContextValue | null>(() => {
    const data = head.data;
    if (!data) return null;
    const leads = data.role === "gameMaster";
    const mode: CampaignViewMode = dossier ? "dossier" : !leads || playerView ? "player" : "normal";
    const term = (termKey: string) => data.terms.find((t) => t.key === termKey)?.value.de ?? termKey;
    return {
      id,
      head: data,
      mode,
      leads,
      canWrite: leads && mode !== "player",
      key,
      request,
      fetch: fetchWithView,
      view,
      sameView,
      trackDraft,
      hasDrafts,
      apply,
      undo: () => step("undo"),
      redo: () => step("redo"),
      reload: () => void client.invalidateQueries({ queryKey: ["campaign", id, viewKey] }),
      term,
      kind: (kindId) => data.kinds.find((k) => k.id === kindId),
      kindLabel: (kindId) => data.kinds.find((k) => k.id === kindId)?.label.de ?? term(kindId),
      entryPath: (entryId) => `/k/${id}/e/${entryId}`,
      dossierDeadline: dossier?.deadline ?? null,
      openDossier,
      closeDossier,
      setPlayerView,
      notice,
      clearNotice: () => setNotice(null),
    };
  }, [
    head.data,
    dossier,
    playerView,
    id,
    key,
    request,
    fetchWithView,
    view,
    sameView,
    trackDraft,
    hasDrafts,
    apply,
    step,
    client,
    viewKey,
    openDossier,
    closeDossier,
    setPlayerView,
    notice,
  ]);

  return <CampaignContext.Provider value={value}>{props.children(head.data, head.error, epoch)}</CampaignContext.Provider>;
}

/**
 * The Date header has whole seconds and may be up to a second old on the server. The local deadline must never lie after the
 * server's, or a request just before it gets 403 instead of the notice about the dossier time.
 */
const dateHeaderMargin = 2000;

/**
 * The expiry from the server as local time, measured from the server's own clock in the response so clock skew does not count,
 * and from when the request was sent, so the way there and back only makes it earlier.
 */
function localDeadline(expires: number, response: Response, sentAt: number): number {
  const server = Date.parse(response.headers.get("Date") ?? "");
  return Number.isNaN(server) ? expires : sentAt + (expires - server) - dateHeaderMargin;
}
